import { Shield, TriangleAlert } from 'lucide-react';
import { useId } from 'react';
import type { Diagnostic, NetworkKinds } from '../../api/types';
import { strings } from '../../strings';
import { NetworkDiagnostic } from '../lists/parts';
import { AllowedEgressList } from './AllowedEgressList';
import { GCP_CLOUD_NAT, money, type FirewallForm } from './helpers';

const g = strings.networks.gcp;
const t = strings.networks.topology;
type Entry = NetworkKinds['firewalls'][number];

/** The tiers a GCP firewall card lists: the registry's `standard` and `enterprise` (greyed out by its `tierReasons`). */
function listedTiers(entry: Entry): string[] {
  return entry.tiers;
}

/** The cost lines of Cloud NGFW Standard (spec 11 6.15.35): no fixed price, Cloud NAT with egress, the saving note. */
function costLines(entry: Entry, on: boolean, byHubSpoke: boolean): string[] {
  const asOf = entry.tierCosts?.standard?.asOf ?? Object.values(entry.tierCosts ?? {})[0]?.asOf;
  const lines = asOf ? [g.firewallCost(asOf)] : [];
  if (on) lines.push(g.natCost(money(GCP_CLOUD_NAT)));
  if (on && byHubSpoke) lines.push(g.turnedOnForHubSpoke(money(GCP_CLOUD_NAT)));
  if (!on) lines.push(g.firewallOffHelp);
  return lines;
}

/**
 * Standard, and the tiers that can't be chosen yet greyed out (`aria-disabled`, focusable) with their `tierReasons` text
 * ("Coming later" with Enterprise's price per zone; user decision 2026-11-03).
 */
function TierChoice({
  entry,
  value,
  readOnly,
  name,
  diagnostics,
  onChange,
}: {
  entry: Entry;
  value: string;
  readOnly: boolean;
  name: string;
  diagnostics: Diagnostic[];
  onChange: (tier: string) => void;
}) {
  const ids = useId();
  return (
    <fieldset data-testid="gcp-tiers">
      <legend className="label">{g.tierLegend}</legend>
      {listedTiers(entry).map((tier) => {
        const reason = entry.tierReasons?.[tier] ?? null;
        const reasonId = `${ids}-${tier}-reason`;
        return (
          <label
            key={tier}
            className={`mb-1 flex items-start gap-2 text-sm ${reason ? 'text-fg-muted' : ''}`}
          >
            <input
              type="radio"
              name={name}
              className="mt-1"
              checked={value === tier}
              disabled={readOnly}
              aria-disabled={reason ? true : undefined}
              aria-describedby={reasonId}
              onChange={() => {
                if (!reason) onChange(tier);
              }}
            />
            <span>
              <span className="font-medium">{g.tierLabel[tier] ?? tier}</span>
              <span id={reasonId} className="block text-[12.5px] text-fg-muted">
                {reason ? reason.text : g.tierStandardHelp}
              </span>
            </span>
          </label>
        );
      })}
      {diagnostics.map((d, i) => (
        <p key={i} className="mt-1">
          <NetworkDiagnostic diagnostic={d} />
        </p>
      ))}
    </fieldset>
  );
}

/**
 * Settings → Firewall's Google Cloud card (feature spec 17, phase 6; spec 11 6.15.35): the Cloud NGFW Standard switch with
 * its cost lines, the tiers with Enterprise greyed out, egress through it (on Google Cloud it filters only egress, so
 * turning it on turns egress through it on; Direct needs the firewall off), the exact host names, `EGRESS_NOT_FILTERED`.
 */
export function GcpFirewallCard({
  entry,
  value,
  readOnly,
  turnedOnByChoice,
  diagnosticsAt,
  onChange,
}: {
  entry: Entry;
  value: FirewallForm | null;
  readOnly: boolean;
  /** Choosing hub-spoke turned it on in the form (the saving note). */
  turnedOnByChoice: boolean;
  diagnosticsAt: (path: string) => Diagnostic[];
  onChange: (firewall: FirewallForm | null) => void;
}) {
  const ids = useId();
  const base = '/settings/providers/gcp/firewall';
  const on = !!value?.enabled;
  const through = on && value?.egress === 'through-firewall';
  const tier = value?.tier ?? 'standard';
  const hosts = value?.allowedEgress ?? [];
  const update = (next: Partial<FirewallForm>) =>
    onChange({
      enabled: on,
      tier,
      egress: value?.egress ?? 'through-firewall',
      allowedEgress: hosts,
      ...next,
    });
  const sectionDiagnostics = diagnosticsAt(base).filter(
    (d) => d.path === base && d.code !== 'EGRESS_NOT_FILTERED',
  );
  return (
    <fieldset className="net-provider-block" data-testid="firewall-gcp">
      <legend className="sr-only">{g.firewall}</legend>
      <label className="net-switch-label flex items-center gap-2 font-semibold">
        <input
          type="checkbox"
          role="switch"
          checked={on}
          disabled={readOnly}
          aria-describedby={`${ids}-help`}
          onChange={(e) =>
            e.target.checked
              ? update({ enabled: true, tier: 'standard', egress: 'through-firewall' })
              : onChange(value ? { ...value, enabled: false } : null)
          }
        />
        <Shield aria-hidden className="size-4 text-fg-muted" />
        {g.firewall}
      </label>
      <p id={`${ids}-help`} className="help mt-1" data-testid="gcp-firewall-cost">
        {costLines(entry, on, turnedOnByChoice).map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </p>
      {diagnosticsAt(`${base}/enabled`).map((d, i) => (
        <p key={i} className="mt-1">
          <NetworkDiagnostic diagnostic={d} />
        </p>
      ))}
      {on && (
        <div className="mt-3 grid gap-x-4 gap-y-3 sm:grid-cols-2">
          <TierChoice
            entry={entry}
            value={tier}
            readOnly={readOnly}
            name={`${ids}-tier`}
            diagnostics={diagnosticsAt(`${base}/tier`)}
            onChange={(next) => update({ tier: next })}
          />
          <fieldset>
            <legend className="label">{t.egress}</legend>
            <label className="mb-1 flex items-start gap-2 text-sm">
              <input
                type="radio"
                name={`${ids}-egress`}
                className="mt-1"
                checked={value?.egress === 'through-firewall'}
                disabled={readOnly}
                onChange={() => update({ egress: 'through-firewall' })}
              />
              <span>
                <span className="font-medium">{g.egressThrough}</span>
                <span className="block text-[12.5px] text-fg-muted">{g.egressThroughHelp}</span>
              </span>
            </label>
            <label className="mb-1 flex items-start gap-2 text-sm text-fg-muted">
              <input
                type="radio"
                name={`${ids}-egress`}
                className="mt-1"
                checked={value?.egress === 'direct'}
                disabled={readOnly}
                aria-disabled
                aria-describedby={`${ids}-direct`}
                onChange={() => undefined}
              />
              <span>
                <span className="font-medium">{t.egressDirect}</span>
                <span id={`${ids}-direct`} className="block text-[12.5px]">
                  {g.egressDirectNeedsOff}
                </span>
              </span>
            </label>
            {diagnosticsAt(`${base}/egress`).map((d, i) => (
              <p key={i} className="mt-1">
                <NetworkDiagnostic diagnostic={d} />
              </p>
            ))}
          </fieldset>
          <AllowedEgressList
            hosts={hosts}
            readOnly={readOnly}
            help={g.allowedEgressHelp}
            diagnosticsAt={(i) => diagnosticsAt(`${base}/allowedEgress/${i}`)}
            onChange={(allowedEgress) => update({ allowedEgress })}
          />
        </div>
      )}
      {sectionDiagnostics.map((d, i) => (
        <p key={i} className="mt-2">
          <NetworkDiagnostic diagnostic={d} />
        </p>
      ))}
      {!through && (
        <p className="callout callout-warning mt-3" data-testid="egress-not-filtered">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <span>
            {g.egressNotFiltered} <code className="font-mono text-xs">EGRESS_NOT_FILTERED</code>
          </span>
        </p>
      )}
    </fieldset>
  );
}

/**
 * New network's Cloud NGFW Standard (feature spec 17, phase 6; spec 11 6.15.35): a switch with its cost lines, on in the
 * form when hub-spoke is chosen (with egress through it, which Google Cloud requires), Enterprise greyed out.
 */
export function GcpFirewallChoice({
  entry,
  on,
  byHubSpoke,
  onChange,
}: {
  entry: Entry;
  on: boolean;
  byHubSpoke: boolean;
  onChange: (on: boolean) => void;
}) {
  const ids = useId();
  return (
    <fieldset className="net-provider-block mt-4" data-testid="new-network-gcp-firewall">
      <legend className="sr-only">{g.firewall}</legend>
      <label className="net-switch-label flex items-center gap-2 font-semibold">
        <input
          type="checkbox"
          role="switch"
          checked={on}
          aria-describedby={`${ids}-help`}
          onChange={(e) => onChange(e.target.checked)}
        />
        <Shield aria-hidden className="size-4 text-fg-muted" />
        {g.firewall}
      </label>
      <p id={`${ids}-help`} className="help mt-1">
        {costLines(entry, on, byHubSpoke).map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </p>
      {on && (
        <div className="mt-2">
          <TierChoice
            entry={entry}
            value="standard"
            readOnly={false}
            name={`${ids}-tier`}
            diagnostics={[]}
            onChange={() => undefined}
          />
        </div>
      )}
    </fieldset>
  );
}
